/**
 * ComposerHint — the muted status line above a chat composer that explains, in
 * words, why a send affordance is gated or close to a limit.
 *
 * The problem it solves: a gated control cannot explain itself. `title` on a
 * disabled button is browser-inconsistent and unreachable on touch, and the
 * other affordances a composer gates (Enter, suggestion chips, interactive
 * blocks) swallow the interaction silently. This is the one always-visible
 * explanation, sitting where the user is already looking.
 *
 * THE LIVE REGION STAYS MOUNTED and only its content toggles. That is the whole
 * reason this is a component rather than a `{cond && <p/>}` at each call site:
 * screen readers reliably announce text added to an EXISTING live region, but a
 * region injected together with its text is frequently missed. So the consumer
 * renders it unconditionally and passes nullish `children` when there is nothing
 * to say — zero height, no announcement, region intact:
 *
 *   <ComposerHint tone="pending">{uploading ? UPLOAD_WAIT_COPY : null}</ComposerHint>
 *
 * Policy stays with the consumer. WHEN a hint shows and WHAT it says are product
 * rules that want their own unit tests (an upload still in flight, a full
 * attachment tray, a paste that lost characters at the cap), and they derive
 * from the same predicate that gates `send` so the hint and the gate can never
 * disagree. This owns only the shape.
 *
 * Tones, and the dot that carries them:
 *   - `neutral` (default) — a steady muted dot. A settled state, like a full
 *     attachment tray: nothing is happening, there is simply no more room.
 *   - `pending` — a pulsing warning dot. Work in flight the user is waiting on.
 *     The pulse is `motion-reduce:animate-none`.
 *   - `warning` — a steady warning dot with warning text. Approaching a limit.
 *   - `danger` — a steady destructive dot with destructive text. At the limit,
 *     or something was lost.
 *
 * `srOnlyText` exists for the counter case. A live counter inside a live region
 * announces on every keystroke, so a consumer showing "1,940 / 2,000" passes the
 * digits as `children` with `aria-hidden`-style visual-only treatment and hands
 * the stable SENTENCE to `srOnlyText`, which changes only on threshold
 * transitions.
 */
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";

/* ============================================================================
 * Variants
 * ========================================================================= */

export const composerHintVariants = cva(
  "mb-1.5 flex items-center gap-1.5 px-1 font-sans text-[12px]",
  {
    variants: {
      tone: {
        neutral: "text-muted-foreground",
        pending: "text-muted-foreground",
        warning: "text-warning",
        danger: "text-destructive",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

const dotVariants = cva("h-1.5 w-1.5 shrink-0 rounded-full", {
  variants: {
    tone: {
      neutral: "bg-muted-foreground",
      pending: "bg-warning animate-pulse motion-reduce:animate-none",
      warning: "bg-warning",
      danger: "bg-destructive",
    },
  },
  defaultVariants: { tone: "neutral" },
});

/* ============================================================================
 * Component
 * ========================================================================= */

export interface ComposerHintProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof composerHintVariants> {
  /** The hint copy. Nullish renders nothing but keeps the live region mounted. */
  children?: React.ReactNode;
  /** Hide the leading dot (e.g. a bare character counter). */
  dot?: boolean;
  /**
   * A stable sentence for assistive tech when `children` changes too often to
   * announce (a live counter). When set, the visible row is `aria-hidden` and
   * this rides the live region instead.
   */
  srOnlyText?: string | null;
  /**
   * The hint's copy is still resolving (a policy that has to wait on a fetch,
   * e.g. a per-workspace attachment cap). Holds one line of space so the
   * composer does not jump when the words land.
   *
   * Distinct from the empty state, and the distinction matters here: nullish
   * `children` means "nothing to say" and must render NOTHING while KEEPING the
   * live region mounted, which is what lets the next real hint be announced.
   * `loading` means "something to say, words pending", so it renders a
   * placeholder and stays silent to assistive tech until the copy is real.
   */
  loading?: boolean;
}

export const ComposerHint = React.forwardRef<HTMLDivElement, ComposerHintProps>(
  function ComposerHint(
    { children, tone, dot = true, srOnlyText, loading = false, className, ...props },
    ref,
  ) {
    const hasVisible = children != null && children !== false;
    /*
     * When the consumer supplies srOnlyText, the visible row is decorative to
     * assistive tech and the sr-only span is the announcement. Otherwise the
     * visible copy IS the announcement and no second copy is rendered.
     */
    const splitAnnouncement = srOnlyText !== undefined;

    /*
     * WHERE role="status" SITS depends on which of those two it is, and the
     * live region is kept TIGHT around the announcement either way.
     *
     * In the split case it goes on the sr-only span, NOT the wrapper. Wrapping
     * the whole thing would put the aria-hidden counter inside the live region;
     * a correct AT skips it, but live-region handling of aria-hidden subtrees is
     * inconsistent in practice, and a region whose text is exactly the sentence
     * cannot be got wrong. Both arrangements keep a permanently mounted region
     * whose content toggles, which is the property that makes the announcement
     * land at all.
     */
    /*
     * The placeholder is aria-hidden and carries no announcement: a live region
     * that fires on "loading" and again on the real copy says the same thing
     * twice, and the first time it says nothing useful.
     */
    const row = loading ? (
      <div aria-hidden="true" className={cn(composerHintVariants({ tone }), className)}>
        {dot && <Skeleton className="h-1.5 w-1.5 shrink-0 rounded-full" />}
        <Skeleton className="h-3 w-48 rounded-full" />
      </div>
    ) : hasVisible ? (
      <div
        aria-hidden={splitAnnouncement ? "true" : undefined}
        className={cn(composerHintVariants({ tone }), className)}
      >
        {dot && <span className={dotVariants({ tone })} aria-hidden="true" />}
        {children}
      </div>
    ) : null;

    if (splitAnnouncement) {
      return (
        <div ref={ref} {...props}>
          {row}
          <span role="status" className="sr-only">
            {srOnlyText ?? ""}
          </span>
        </div>
      );
    }

    return (
      <div ref={ref} role="status" {...props}>
        {row}
      </div>
    );
  },
);
